import { useReducer, useState } from 'react';
import { Formik, Form, FormikHelpers } from 'formik';
import { LoginContainer, LoginPanel } from './styled';
import { useNavigate } from 'react-router-dom';
import {
  FormTextField,
  SubmitButton,
  SecureTrustCertificate
} from 'components';
import { useAuthentication } from '../../hooks/useAuthentication';
import backgroundImage from '../../assets/background.jpeg';
import ventraLogo from '../../assets/ventra-logo.png';
import { MFAPhone } from './MFAPhone/MFAPhone';
import { MFA } from './MFA/MFA';
import { FindHelp } from './FindHelp';
import { usePartnerGetMaintenanceMode } from 'hooks/requests';
import { ROUTES } from 'constants/routes';
import { ErrorResponse } from 'types/network';
import { User } from 'types/user';
import { theme } from 'lib/theme';
import { useSnackbar } from 'notistack';
import { useLogin } from 'hooks/requests/useLogin';

enum StepTypes {
  LOGIN = 'LOGIN',
  MFA_PHONE = 'MFA_PHONE',
  MFA = 'MFA'
}

export default function Login() {
  const { mutateAsync: login } = useLogin();
  const navigate = useNavigate();
  const { setToken, setUserInfo } = useAuthentication();
  const snackbar = useSnackbar();
  const [user, setUser] = useState({} as unknown as User);

  const { data: inMaintenance = false, refetch } = usePartnerGetMaintenanceMode(
    {
      onError: err => {
        snackbar.enqueueSnackbar(
          err.response?.data?.Error || ROUTES.PartnerGetMaintenanceMode,
          { variant: 'error' }
        );
      }
    }
  );

  const [state, dispatch] = useReducer(loginReducer, {
    step: StepTypes.LOGIN
  });

  interface FormValues {
    username: string;
    password: string;
  }
  async function _handleLogin(
    values: FormValues,
    actions: FormikHelpers<FormValues>
  ) {
    const { username, password } = values;
    try {
      const user = await login({ username, password });
      setUser(user);
      setToken(user.token);
      setUserInfo(JSON.stringify(user));
      const validPhoneRegex = /^(\d{10})$/;
      if (user.requireMFA && user.isEmailMFA) {
        gotoMFA();
      } else if (user.requireMFA && !validPhoneRegex.test(user.phoneNumber)) {
        gotoMFAPhone();
      } else if (user.requireMFA) {
        gotoMFA();
      } else if (!user.showMyEncounters && user.showMyTransactions) {
        navigate('/MyTransactionsSummary');
      } else {
        navigate('/MyEncounters');
      }
    } catch (err) {
      snackbar.enqueueSnackbar(
        (err as ErrorResponse).response?.data.Error ||
          'oops! an error occurred',
        { variant: 'error' }
      );
      refetch();
    }
    actions.setSubmitting(false);
  }

  const gotoMFAPhone = () => dispatch({ type: StepTypes.MFA_PHONE });
  const gotoMFA = () => dispatch({ type: StepTypes.MFA });

  return (
    <LoginContainer src={backgroundImage}>
      <div>
        <div className="working" />
        <div className="ReverseWorking" />
        <LoginPanel>
          <img
            alt="background containing doctor filling out paper"
            src={ventraLogo}
            style={{
              width: '275px',
              marginBottom: '12px',
              marginTop: inMaintenance ? 0 : '40px'
            }}
          />

          {inMaintenance ? (
            <h2
              style={{
                textAlign: 'center'
              }}
            >
              The Partner Portal is undergoing routine maintenance. Please try
              again later.
            </h2>
          ) : null}

          {state.step === StepTypes.LOGIN && !inMaintenance ? (
            <Formik<FormValues>
              onSubmit={_handleLogin}
              initialValues={{
                username: '',
                password: ''
              }}
              render={({ isValidating, isSubmitting }) => {
                return (
                  <Form
                    style={{
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        height: '180px',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        marginTop: '12px'
                      }}
                    >
                      <FormTextField
                        idx="login-username"
                        name="username"
                        label="Username"
                        isRequired={true}
                      />
                      <FormTextField
                        idx="login-password"
                        name="password"
                        label="Password"
                        isRequired={true}
                        type="password"
                      />
                      <SubmitButton
                        id="login-submit"
                        loading={!!(isValidating || isSubmitting)}
                        disabled={!!(isValidating || isSubmitting)}
                      >
                        Login
                      </SubmitButton>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        width: '100%',
                        padding: '15px 0'
                      }}
                    >
                      <a
                        style={{
                          textDecoration: 'underline',
                          marginBottom: '10px',
                          cursor: 'pointer',
                          color: theme.palette.primary.main
                        }}
                        onClick={() => navigate('/ForgotPassword')}
                      >
                        Forgot Password
                      </a>
                      <FindHelp />

                      <SecureTrustCertificate
                        style={{
                          marginTop: '12px'
                        }}
                      />
                    </div>
                  </Form>
                );
              }}
            />
          ) : null}
          {state.step === StepTypes.MFA_PHONE ? (
            <MFAPhone gotoMFA={gotoMFA} />
          ) : null}
          {state.step === StepTypes.MFA ? (
            <MFA
              user={user}
              setErrorMessage={() => {}}
              setOpenSnackBar={() => {}}
            />
          ) : null}
        </LoginPanel>
      </div>
    </LoginContainer>
  );
}

interface LoginState {
  step: StepTypes;
}

function loginReducer(state: LoginState, action: { type: StepTypes }) {
  switch (action.type) {
    case StepTypes.LOGIN:
      return {
        ...state,
        step: StepTypes.LOGIN
      };
    case StepTypes.MFA_PHONE:
      return {
        ...state,
        step: StepTypes.MFA_PHONE
      };
    case StepTypes.MFA:
      return {
        ...state,
        step: StepTypes.MFA
      };
    default:
      throw new Error(`${action.type} is not a valid action for loginReducer.`);
  }
}
